import API from '@/api/apiUrls'
import { useApi } from '@/api/useApi'
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import type { Project } from '@/stores/type/project.type'

import { useProjectUserStore } from '@/stores/projectUser'
import { useDjangoAuthStore } from '@/stores/authDjango'
import { useAuthorizationStore } from '@/stores/authorization'
import { useBaseStore } from '@haskoning/vue-basestore'
import type { ProjectUserType } from '@/stores/type/projectUser.type'
import type { ProjectPermissionType } from '@/stores/type/authorization.type'

const { api } = useApi()

const baseUrl = API.CORE.PROJECTS.INDEX

export const useProjectStore = defineStore('project', () => {
  const baseStore = useBaseStore<Project>({ service: baseUrl, api })

  const servicePermissionProjects = ref<ProjectPermissionType[]>([])
  const filterFields = ref(['name', 'location', 'client', 'description'])

  const getCurrentProject = computed(() => {
    return baseStore.currentItem ? baseStore.currentItem : null
  })

  const getChangePermission = computed(() => (id: number) => {
    if (!servicePermissionProjects.value.length) return true
    const permissions =
      servicePermissionProjects.value.find((item) => item.id === id)?.permissions ?? []
    return permissions.includes('change')
  })

  const getDeletePermission = computed(() => (id: number) => {
    if (!servicePermissionProjects.value.length) return true
    const permissions =
      servicePermissionProjects.value.find((item) => item.id === id)?.permissions ?? []
    return permissions.includes('delete')
  })

  const fieldsFiltered = computed(() => {
    return baseStore.fieldsList.value.filter((field) =>
      filterFields.value.includes(field.parameter)
    )
  })

  async function servicePermissionItems() {
    const projectUserStore = useProjectUserStore()
    const authStore = useDjangoAuthStore()
    const authorizationStore = useAuthorizationStore()

    const userId = authStore.user?.id
    await projectUserStore.fetchItems({ user: userId })
    const projectUserItems = projectUserStore.items || []

    const roleItems = projectUserItems.map((item: ProjectUserType) => {
      return {
        role: item.role,
        id: item.project
      }
    })
    servicePermissionProjects.value = await authorizationStore.getServicePermissionByRole(
      roleItems,
      ['change_project', 'delete_project']
    )
  }

  function resetAll() {
    baseStore.reset()
  }

  return {
    ...baseStore,
    resetAll,
    getCurrentProject,
    getChangePermission,
    getDeletePermission,
    fieldsFiltered,
    servicePermissionItems,
    filterFields
  }
})
